/**
 * CopyableTable - Table wrapper with copy-to-clipboard functionality.
 * Extracts table data as TSV for pasting into spreadsheets.
 */

import React, { useEffect, useRef, useState } from 'react';

import { logger } from '@/lib/logger';

interface CopyableTableProps {
  children: React.ReactNode;
}

/**
 * Table wrapper component that adds a copy-to-clipboard button.
 * Extracts table data as tab-separated values (TSV) for easy pasting into spreadsheets.
 * Button appears on hover in top-right corner of table.
 */
export const CopyableTable: React.FC<CopyableTableProps> = React.memo(
  ({ children }) => {
    const [copied, setCopied] = useState(false);
    const tableRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleCopy = async () => {
      if (!tableRef.current) {
        return;
      }

      const table = tableRef.current.querySelector('table');
      if (!table) {
        return;
      }

      // Extract table data as tab-separated values
      const rows: string[] = [];
      Array.from(table.querySelectorAll('tr')).forEach(tr => {
        const cells: string[] = [];
        Array.from(tr.querySelectorAll('th, td')).forEach(cell => {
          cells.push((cell.textContent || '').trim());
        });
        rows.push(cells.join('\t'));
      });

      const tsv = rows.join('\n');
      try {
        await navigator.clipboard.writeText(tsv);
        setCopied(true);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        logger.warn('Clipboard copy failed', { error });
      }
    };

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <div className='my-4 first:mt-0 w-full min-w-0' ref={tableRef}>
        <div className='overflow-x-auto overflow-y-hidden'>
          <div className='rounded-xl ring-1 ring-border-secondary shadow-xs relative group'>
            <button
              type='button'
              onClick={handleCopy}
              className='absolute top-2 right-2 z-10 p-1.5 rounded-md bg-bg-primary/90 border border-border-secondary shadow-sm opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-bg-secondary transition-all cursor-pointer'
              title='Copy table to clipboard'
              aria-label='Copy table to clipboard'
            >
              {copied ? (
                <svg
                  className='size-4 text-success'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M5 13l4 4L19 7'
                  />
                </svg>
              ) : (
                <svg
                  className='size-4 text-tertiary'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
                  />
                </svg>
              )}
            </button>
            <table className='border-collapse min-w-full'>{children}</table>
          </div>
        </div>
      </div>
    );
  }
);

CopyableTable.displayName = 'CopyableTable';
